import type { Rank } from "@rivaled/shared";
import {
  Brain,
  ChevronDown,
  ClipboardCheck,
  Crosshair,
  Gauge,
  Puzzle,
  ShieldBan,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";

import { cn } from "../../lib/utils";
import { Button, Input } from "../ui";
import { rankVarAlpha } from "../ui/RankIcon";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

type Props = {
  form: QueueSettingsForm;
};

type SubsectionConfig = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  accentRank?: Rank;
  defaultOpen?: boolean;
};

const subsections: SubsectionConfig[] = [
  {
    id: "elo-window",
    icon: Crosshair,
    title: "ELO Window",
    description: "Rating search range that expands over time to reduce wait",
    accentRank: "diamond",
    defaultOpen: true,
  },
  {
    id: "match-quality",
    icon: Gauge,
    title: "Match Quality",
    description: "Balance between skill parity and queue speed",
    accentRank: "gold",
  },
  {
    id: "party-rating",
    icon: Users,
    title: "Party Rating",
    description: "How party member ratings blend into a group rating",
    accentRank: "platinum",
  },
  {
    id: "trueskill",
    icon: Brain,
    title: "TrueSkill",
    description: "Core skill estimation parameters (mu, sigma, beta, tau)",
    accentRank: "prime",
  },
  {
    id: "bans",
    icon: ShieldBan,
    title: "Queue Bans",
    description: "Auto-bans for dodging with escalating penalties",
    accentRank: "champion",
  },
  {
    id: "reporting",
    icon: ClipboardCheck,
    title: "Reporting",
    description: "Time limit for post-match result submission",
    accentRank: "silver",
  },
  {
    id: "composition",
    icon: Puzzle,
    title: "Composition",
    description: "Required roles per team (e.g. Tank, Healer, DPS)",
    accentRank: "bronze",
  },
];

function Subsection({ config, children }: { config: SubsectionConfig; children: React.ReactNode }) {
  const [open, setOpen] = useState(config.defaultOpen ?? false);
  const Icon = config.icon;
  const rank = config.accentRank;

  return (
    <div
      className="rounded-lg border border-surface-700/40 overflow-hidden transition-all duration-200"
      style={
        rank
          ? {
              borderColor: open ? rankVarAlpha(rank, 25) : undefined,
            }
          : undefined
      }
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200",
          open ? "bg-surface-800/80" : "bg-surface-800/40 hover:bg-surface-800/60",
        )}
      >
        <div
          className="flex items-center justify-center size-8 rounded-lg shrink-0 transition-colors duration-200"
          style={
            rank
              ? {
                  backgroundColor: rankVarAlpha(rank, open ? 18 : 10),
                  color: `var(--color-rank-${rank})`,
                }
              : { backgroundColor: "rgb(var(--color-surface-700) / 0.5)" }
          }
        >
          <Icon className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-white">{config.title}</h4>
          <p className="text-2xs text-surface-500 leading-snug">{config.description}</p>
        </div>
        <ChevronDown
          className={cn(
            "size-4 text-surface-500 shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>

      {/* Content */}
      {open && <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">{children}</div>}
    </div>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {label && (
        <div>
          <p className="text-xs font-semibold text-surface-300">{label}</p>
          {hint && <p className="text-2xs text-surface-500">{hint}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

export function MatchmakingSection({ form }: Props) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-surface-500">
        Fine-tune the matchmaking algorithm. Leave blank to use system defaults — only change these
        if you understand the impact.
      </p>

      {/* ELO Window */}
      <Subsection config={subsections[0]}>
        <FieldGroup>
          <Input
            label="Base Range"
            type="number"
            value={form.mmEloBaseRange}
            onChange={(e) => form.setMmEloBaseRange(e.target.value)}
            placeholder="200"
            suffix="ELO"
          />
          <Input
            label="Expansion Step"
            type="number"
            value={form.mmEloExpansionStep}
            onChange={(e) => form.setMmEloExpansionStep(e.target.value)}
            placeholder="50"
            suffix="ELO"
          />
          <Input
            label="Max Disparity"
            type="number"
            value={form.mmEloMaxDisparity}
            onChange={(e) => form.setMmEloMaxDisparity(e.target.value)}
            placeholder="1000"
            suffix="ELO"
          />
          <Input
            label="Expansion Interval"
            type="number"
            value={form.mmEloExpansionInterval}
            onChange={(e) => form.setMmEloExpansionInterval(e.target.value)}
            placeholder="30"
            suffix="sec"
          />
        </FieldGroup>
      </Subsection>

      {/* Match Quality */}
      <Subsection config={subsections[1]}>
        <div className="space-y-4">
          <FieldGroup
            label="Scoring Weights"
            hint="How ELO closeness vs wait time affects match scoring"
          >
            <Input
              label="ELO Weight"
              type="number"
              step="0.01"
              value={form.mmScoringEloWeight}
              onChange={(e) => form.setMmScoringEloWeight(e.target.value)}
              placeholder="0.6"
            />
            <Input
              label="Wait Weight"
              type="number"
              step="0.01"
              value={form.mmScoringWaitWeight}
              onChange={(e) => form.setMmScoringWaitWeight(e.target.value)}
              placeholder="0.4"
            />
          </FieldGroup>
          <FieldGroup label="Thresholds">
            <Input
              label="Min Match Quality"
              type="number"
              step="0.01"
              value={form.mmMinMatchQuality}
              onChange={(e) => form.setMmMinMatchQuality(e.target.value)}
              placeholder="0.2"
            />
            <Input
              label="Quality Divisor"
              type="number"
              value={form.mmQualityDivisor}
              onChange={(e) => form.setMmQualityDivisor(e.target.value)}
              placeholder="500"
            />
          </FieldGroup>
        </div>
      </Subsection>

      {/* Party Rating */}
      <Subsection config={subsections[2]}>
        <FieldGroup hint="Weights should sum to 1.0. Higher 'Highest' weight penalizes boosting.">
          <Input
            label="Highest Weight"
            type="number"
            step="0.01"
            value={form.mmPartyHighestWeight}
            onChange={(e) => form.setMmPartyHighestWeight(e.target.value)}
            placeholder="0.7"
          />
          <Input
            label="Average Weight"
            type="number"
            step="0.01"
            value={form.mmPartyAverageWeight}
            onChange={(e) => form.setMmPartyAverageWeight(e.target.value)}
            placeholder="0.3"
          />
        </FieldGroup>
      </Subsection>

      {/* TrueSkill */}
      <Subsection config={subsections[3]}>
        <div className="space-y-4">
          <FieldGroup label="Initial Values" hint="Starting skill estimate for new players">
            <Input
              label="Default Mu (μ)"
              type="number"
              step="0.1"
              value={form.mmTsDefaultMu}
              onChange={(e) => form.setMmTsDefaultMu(e.target.value)}
              placeholder="25.0"
            />
            <Input
              label="Default Sigma (σ)"
              type="number"
              step="0.1"
              value={form.mmTsDefaultSigma}
              onChange={(e) => form.setMmTsDefaultSigma(e.target.value)}
              placeholder="8.333"
            />
          </FieldGroup>
          <FieldGroup label="Algorithm Parameters">
            <Input
              label="Beta (β)"
              type="number"
              step="0.1"
              value={form.mmTsBeta}
              onChange={(e) => form.setMmTsBeta(e.target.value)}
              placeholder="4.166"
            />
            <Input
              label="Tau (τ)"
              type="number"
              step="0.01"
              value={form.mmTsTau}
              onChange={(e) => form.setMmTsTau(e.target.value)}
              placeholder="0.0833"
            />
            <Input
              label="Draw Probability"
              type="number"
              step="0.01"
              value={form.mmTsDrawProbability}
              onChange={(e) => form.setMmTsDrawProbability(e.target.value)}
              placeholder="0.0"
            />
            <Input
              label="Display ELO Multiplier"
              type="number"
              value={form.mmTsDisplayEloMultiplier}
              onChange={(e) => form.setMmTsDisplayEloMultiplier(e.target.value)}
              placeholder="40"
            />
          </FieldGroup>
        </div>
      </Subsection>

      {/* Bans */}
      <Subsection config={subsections[4]}>
        <FieldGroup hint="Ban duration = Base × Escalation ^ (recent offenses - 1), capped at Max.">
          <Input
            label="Base Duration"
            type="number"
            value={form.mmBansBaseDuration}
            onChange={(e) => form.setMmBansBaseDuration(e.target.value)}
            placeholder="300"
            suffix="sec"
          />
          <Input
            label="Escalation Multiplier"
            type="number"
            step="0.1"
            value={form.mmBansEscalation}
            onChange={(e) => form.setMmBansEscalation(e.target.value)}
            placeholder="3"
            suffix="×"
          />
          <Input
            label="History Window"
            type="number"
            value={form.mmBansHistoryWindow}
            onChange={(e) => form.setMmBansHistoryWindow(e.target.value)}
            placeholder="24"
            suffix="hrs"
          />
          <Input
            label="Max Ban Duration"
            type="number"
            value={form.mmBansMaxDuration}
            onChange={(e) => form.setMmBansMaxDuration(e.target.value)}
            placeholder="86400"
            suffix="sec"
          />
        </FieldGroup>
      </Subsection>

      {/* Reporting */}
      <Subsection config={subsections[5]}>
        <Input
          label="Timeout"
          type="number"
          value={form.mmReportingTimeout}
          onChange={(e) => form.setMmReportingTimeout(e.target.value)}
          placeholder="300"
          suffix="sec"
        />
      </Subsection>

      {/* Composition */}
      <Subsection config={subsections[6]}>
        <div className="space-y-3">
          {form.compositionSlots.length === 0 ? (
            <p className="text-xs text-surface-500 italic">
              No role requirements — any player can fill any slot.
            </p>
          ) : (
            <div className="space-y-2">
              {form.compositionSlots.map((slot, i) => (
                // oxlint-disable-next-line react/no-array-index-key -- composition slots have no stable ID
                <div key={i} className="flex items-center gap-2">
                  <Input
                    placeholder="Role name"
                    value={slot.role}
                    onChange={(e) => {
                      const next = [...form.compositionSlots];
                      next[i] = { ...next[i], role: e.target.value };
                      form.setCompositionSlots(next);
                    }}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={slot.count}
                    onChange={(e) => {
                      const next = [...form.compositionSlots];
                      next[i] = { ...next[i], count: e.target.value };
                      form.setCompositionSlots(next);
                    }}
                    className="w-20"
                  />
                  <button
                    type="button"
                    onClick={() => form.setCompositionSlots((s) => s.filter((_, j) => j !== i))}
                    className="p-1.5 rounded-md text-surface-500 hover:text-error-400 hover:bg-error-500/10 transition-colors"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
              {(() => {
                const total = form.compositionSlots.reduce(
                  (sum, s) => sum + (Number(s.count) || 0),
                  0,
                );
                const ts = Number(form.teamSize) || 0;
                if (ts > 0 && total > 0 && total !== ts) {
                  return (
                    <p className={`text-xs ${total > ts ? "text-error-400" : "text-amber-400"}`}>
                      Slot total ({total}) {total > ts ? "exceeds" : "is less than"} team size ({ts}
                      )
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => form.setCompositionSlots((s) => [...s, { role: "", count: "1" }])}
          >
            + Add Role
          </Button>
        </div>
      </Subsection>
    </div>
  );
}
